import { ReactElement, useCallback } from 'react';
import { Button, Spin } from 'antd';
import { ExclamationCircleOutlined, WalletOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import CustomModal from '@/components/CustomModal';
import AddressIcon from '@/components/AddressIcon';
import { sensitiveAddress } from '@/utils';
import { useAppDispatch, useWallet } from '@/services';
import { dismissLoginError, link } from '@/services/reducers/wallet';
import { shouldShowWalletLoginModal } from '@/services/wallet-login-modal';
import styles from '@/styles/components/wallet-login-modal.module.less';

const WalletLoginModal = (): ReactElement => {
    const { t } = useTranslation('wallet', { keyPrefix: 'login-modal' });
    const dispatch = useAppDispatch();
    const wallet = useWallet();
    const { address, chainId, chain, type, connected, logged, loginPending, loginError } = wallet;
    const open = shouldShowWalletLoginModal({ connected, logged, loginPending, loginError });
    const isError = !!loginError && !loginPending;

    const retryLogin = useCallback(() => {
        if (!address || !chain) return;
        dispatch(
            link({
                chainId,
                chain,
                address,
                connected: true,
                logged: false,
                type,
            })
        );
    }, [address, chain, chainId, dispatch, type]);

    const close = useCallback(() => {
        if (loginPending) return;
        dispatch(dismissLoginError());
    }, [dispatch, loginPending]);

    const errorMessage = loginError?.message || t('unknown-error');

    return (
        <CustomModal
            centered
            destroyOnClose
            open={open}
            title={t(isError ? 'error-title' : 'title')}
            width={460}
            closable={isError}
            maskClosable={false}
            onCancel={close}
            footer={
                isError
                    ? [
                          <Button key="cancel" onClick={close}>
                              {t('cancel')}
                          </Button>,
                          <Button key="retry" type="primary" onClick={retryLogin} disabled={!address || !chain}>
                              {t('retry')}
                          </Button>,
                      ]
                    : null
            }
            className={styles['wallet-login-modal']}
        >
            <div className={styles['content']}>
                <div className={styles['state-icon']}>{isError ? <ExclamationCircleOutlined /> : <Spin indicator={<WalletOutlined spin />} />}</div>
                <div className={styles['message']}>
                    <h3>{t(isError ? 'error-heading' : 'heading')}</h3>
                    <p>{isError ? errorMessage : t('description')}</p>
                </div>
                <div className={styles['wallet-info']}>
                    <div className={styles['address-row']}>
                        <span className={styles['avatar']}>{address ? <AddressIcon address={address} /> : <WalletOutlined />}</span>
                        <span>{address ? sensitiveAddress(address) : t('unknown-address')}</span>
                    </div>
                    <div className={styles['chain-row']}>
                        <span>{t('network')}</span>
                        <strong>{chain?.label || chain?.name || t('unknown-network')}</strong>
                    </div>
                </div>
            </div>
        </CustomModal>
    );
};

export default WalletLoginModal;

